import React, { useState, useEffect, useMemo } from "react";
import {
    Card,
    Row,
    Col,
    Typography,
    Empty,
    Divider,
    Statistic,
    Tag,
    Select,
    DatePicker,
    Button,
    Spin,
    message,
    Modal,
} from "antd";
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line,
} from "recharts";
import {
    BarChartOutlined,
    TeamOutlined,
    CalendarOutlined,
    FilterOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import api from "../../../services/api";
import dayjs from "dayjs";
import { MoodResponse, MoodQuestion } from "./MoodMeterDashboard";
import Datatable from "../../../core/common/dataTable";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface Props {
    selectedCompany: {
        id: string | null;
        type: string | null;
        name: string;
    };
    dateRange: [dayjs.Dayjs, dayjs.Dayjs];
    onDateRangeChange?: (range: [dayjs.Dayjs, dayjs.Dayjs]) => void;
    departments?: any[];
    questions?: any[];
    disabled?: boolean;
}

// Define getMoodColor BEFORE the component
const getMoodColor = (mood: string) => {
    const colors: Record<string, string> = {
        'Excellent': '#52c41a',
        'Good': '#1890ff',
        'Neutral': '#faad14',
        'Stressed': '#ff7a45',
        'Overwhelmed': '#ff4d4f',
    };
    return colors[mood] || '#8884d8';
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const MoodAnalytics: React.FC<Props> = ({
    selectedCompany,
    dateRange,
    onDateRangeChange,
    departments = [],
    questions = [],
}) => {
    const signature = localStorage.getItem("signature");
    const [analyticsData, setAnalyticsData] = useState<any>(null);
    const [responses, setResponses] = useState<MoodResponse[]>([]);
    const [selectedQuestion, setSelectedQuestion] = useState<string>("all");
    const [selectedDepartment, setSelectedDepartment] = useState<string>("all");
    const [loading, setLoading] = useState<boolean>(true);
    const [exporting, setExporting] = useState<boolean>(false);
    const [filteredDepartments, setFilteredDepartments] = useState<any[]>([]);
    const [isUserDetailsModalVisible, setIsUserDetailsModalVisible] = useState(false);
    const [selectedMood, setSelectedMood] = useState<string | null>(null);

    // Fetch Mood Analytics API
    const fetchMoodAnalytics = async () => {
        setLoading(true);
        try {
            const payload: any = {
                signature,
                company_id: selectedCompany.id,
                company_type: selectedCompany.type,
                start_date: dateRange[0].format("YYYY-MM-DD"),
                end_date: dateRange[1].format("YYYY-MM-DD"),
            };

            // Only add question_id to payload if we have a valid question selected
            if (selectedQuestion !== "no-questions" && selectedQuestion !== "all") {
                payload.question_id = parseInt(selectedQuestion);
            }

            if (selectedDepartment !== "all") {
                payload.department_id = parseInt(selectedDepartment);
            }

            const response = await api.post("userService/getMoodAnalytics", payload);

            if (response.data?.message?.[0]?.status === true) {
                setAnalyticsData(response?.data?.message?.[0]?.data);
            } else {
                message.error(response.data?.msg || "Failed to load analytics");
            }
        } catch (error) {
            console.error("Error fetching analytics:", error);
            message.error("Error loading analytics data");
        } finally {
            setLoading(false);
        }
    };

    // Fetch Detailed Responses
    const fetchMoodResponses = async () => {
        try {
            const payload: any = {
                signature,
                company_id: selectedCompany.id,
                company_type: selectedCompany.type,
                start_date: dateRange[0].format("YYYY-MM-DD"),
                end_date: dateRange[1].format("YYYY-MM-DD"),
            };

            // Only add question_id to payload if we have a valid question selected
            if (selectedQuestion !== "no-questions" && selectedQuestion !== "all") {
                payload.question_id = parseInt(selectedQuestion);
            }

            if (selectedDepartment !== "all") {
                payload.department_id = parseInt(selectedDepartment);
            }

            const response = await api.post("userService/getMoodResponses", payload);

            if (response.data?.message?.[0]?.status === true) {
                const data = response?.data?.message?.[0]?.data?.responses || [];
                setResponses(data);
                const uniqueDeptIds = Array.from(new Set(data.map((r: any) => r.department_id)));
                const filtered = [
                    { id: "all", name: "All Departments", key: "all", Department: "All Departments" },
                    ...departments.filter(dept =>
                        uniqueDeptIds.includes(dept.key) && dept.id !== "all"
                    )
                ];
                setFilteredDepartments(filtered);
            }
        } catch (error) {
            console.error("Error fetching responses:", error);
        }
    };

    // Export Data
    const handleExportData = async () => {
        setExporting(true);
        try {
            const payload: any = {
                signature,
                company_id: selectedCompany.id,
                company_type: selectedCompany.type,
                start_date: dateRange[0].format("YYYY-MM-DD"),
                end_date: dateRange[1].format("YYYY-MM-DD"),
            };

            const response = await api.post("userService/getMoodResponses", payload);

            if (response.data?.message?.[0]?.status === true) {
                const data = response?.data?.message?.[0]?.data?.responses || [];

                if (data.length === 0) {
                    message.warning("No data to export");
                    return;
                }

                // Convert to CSV
                const csvRows = [];
                const headers = ["Date", "Question", "Mood", "Score", "Department", "Note"];
                csvRows.push(headers.join(","));

                data.forEach((item: any) => {
                    const row = [
                        dayjs(item.createdAt).format("YYYY-MM-DD HH:mm"),
                        item.question?.question_text || "Unknown",
                        item.mood_text,
                        item.score,
                        departments.find(d => d.key === item.department_id)?.Department || "Unknown",
                        `"${(item.note || "").replace(/"/g, '""')}"`
                    ];
                    csvRows.push(row.join(","));
                });

                const csvString = csvRows.join("\n");
                const blob = new Blob([csvString], { type: "text/csv" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `mood-data-${dayjs().format("YYYY-MM-DD")}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);

                message.success("Data exported successfully");
            }
        } catch (error) {
            console.error("Error exporting data:", error);
            message.error("Failed to export data");
        } finally {
            setExporting(false);
        }
    };

    // Initialize data
    useEffect(() => {
        if (selectedCompany.id && selectedCompany.type) {
            Promise.all([
                fetchMoodAnalytics(),
                fetchMoodResponses(),
            ]);
        }
    }, [selectedCompany, dateRange, selectedQuestion, selectedDepartment]);
    useEffect(() => {
        if (questions && questions.length > 0) {
            setSelectedQuestion(questions[0].id.toString());
        } else {
            setSelectedQuestion("no-questions");
        }
    }, [questions]);

    // Prepare chart data from analytics
    const { pieChartData, barChartData, departmentData, lineChartData } = useMemo(() => {
        if (!analyticsData) {
            return { pieChartData: [], barChartData: [], departmentData: [], lineChartData: [] };
        }

        // Pie Chart Data
        const pieData = Object.entries(analyticsData.mood_distribution || {}).map(([name, value]: [string, any]) => ({
            name,
            value,
            color: getMoodColor(name),
        }));

        // Bar Chart Data (Last 7 days)
        let dailyEntries = [];
        if (Array.isArray(analyticsData.daily_trends)) {
            dailyEntries = analyticsData.daily_trends
                .sort((a: any, b: any) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
                .slice(-7);
        } else {
            dailyEntries = Object.entries(analyticsData.daily_trends || {})
                .sort(([dateA], [dateB]) => dayjs(dateA).valueOf() - dayjs(dateB).valueOf())
                .slice(-7);
        }

        const barData = dailyEntries.map((entry: any) => {
            const date = entry.date || entry[0];
            const data = entry.average_score !== undefined ? entry : entry[1];

            return {
                date: dayjs(date).format("MMM D"),
                avgScore: parseFloat(data.average_score) || 0,
                responses: data.total || 0,
            };
        });

        // Department Data
        let deptData = [];
        if (Array.isArray(analyticsData.department_breakdown)) {
            deptData = analyticsData.department_breakdown
                .map((data: any) => ({
                    department: departments.find(d => d.key == data.department_id)?.Department || `Dept ${data.department_id}`,
                    avgScore: parseFloat(data.average_score) || 0,
                    responses: data.total || 0,
                }))
                .filter((dept: any) => dept.responses > 0)
                .sort((a: any, b: any) => b.avgScore - a.avgScore);
        } else {
            deptData = Object.entries(analyticsData.department_breakdown || {})
                .map(([deptId, data]: [string, any]) => ({
                    department: departments.find(d => d.key == deptId)?.Department || `Dept ${deptId}`,
                    avgScore: parseFloat(data.average_score) || 0,
                    responses: data.total || 0,
                }))
                .filter(dept => dept.responses > 0)
                .sort((a, b) => b.avgScore - a.avgScore);
        }

        // Line Chart Data (Trend)
        let lineData = [];
        if (Array.isArray(analyticsData.daily_trends)) {
            lineData = analyticsData.daily_trends
                .sort((a: any, b: any) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
                .map((data: any) => ({
                    date: dayjs(data.date).format("MMM D"),
                    score: parseFloat(data.average_score) || 0,
                    responses: data.total || 0,
                }));
        } else {
            lineData = Object.entries(analyticsData.daily_trends || {})
                .sort(([dateA], [dateB]) => dayjs(dateA).valueOf() - dayjs(dateB).valueOf())
                .map(([date, data]: [string, any]) => ({
                    date: dayjs(date).format("MMM D"),
                    score: parseFloat(data.average_score) || 0,
                    responses: data.total || 0,
                }));
        }

        return {
            pieChartData: pieData,
            barChartData: barData,
            departmentData: deptData,
            lineChartData: lineData
        };
    }, [analyticsData, departments]);

    const column = [
        {
            title: 'User',
            dataIndex: 'full_name',
            key: 'full_name',
            align: 'left',
            render: (text: string, record: any) => (
                <div className="d-flex align-items-center gap-2">
                    <img
                        src={record.profile_pic || 'https://via.placeholder.com/32'}
                        alt={text}
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            objectFit: 'cover'
                        }}
                    />
                    <div className="d-flex flex-column">
                        <strong>{text}</strong>
                        <small className="text-muted">{record.email}</small>
                    </div>
                </div>
            ),
        },
        {
            title: 'Department',
            dataIndex: 'department_id',
            key: 'department_id',
            align: 'center',
            render: (deptId: number) => {
                const dept = departments.find(d => d.key === deptId);
                return dept?.Department || `Dept ${deptId}`;
            },
        },
        {
            title: 'Mood',
            key: 'mood',
            align: 'center',
            render: (_: any, record: any) => {
                // If a mood filter is active, show only that mood
                if (selectedMood && record.moods?.[selectedMood]) {
                    return (
                        <Tag color={getMoodColor(selectedMood)}>
                            {selectedMood} ({record.moods[selectedMood].count})
                        </Tag>
                    );
                }
                // Otherwise show all moods for this user
                const userMoods = Object.entries(record.moods || {})
                    .filter(([_, val]: [string, any]) => val?.count > 0)
                    .sort(([, a]: [string, any], [, b]: [string, any]) => b.count - a.count);
                return userMoods.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                        {userMoods.map(([mood, val]: [string, any]) => (
                            <Tag key={mood} color={getMoodColor(mood)} style={{ margin: 0 }}>
                                {mood} ({val.count})
                            </Tag>
                        ))}
                    </div>
                ) : '-';
            },
        },
        // {
        //     title: 'Responses',
        //     dataIndex: 'responses_count',
        //     key: 'responses_count',
        //     align: 'center',
        //     sorter: (a: any, b: any) => a.responses_count - b.responses_count,
        // },
        // {
        //     title: 'Avg Score',
        //     dataIndex: 'average_score',
        //     key: 'average_score',
        //     align: 'center',
        //     render: (score: string) => {
        //         const numScore = parseFloat(score) || 0;
        //         const color = numScore >= 3.5 ? '#52c41a' : numScore >= 2.5 ? '#faad14' : '#ff4d4f';
        //         return <Text strong style={{ color }}>{numScore.toFixed(1)}/5</Text>;
        //     },
        //     sorter: (a: any, b: any) => parseFloat(a.average_score) - parseFloat(b.average_score),
        // },
        {
            title: 'Response Date(s)',
            dataIndex: 'response_dates',
            key: 'response_dates',
            align: 'center',
            render: (dates: any[], record: any) => {
                if (!dates || dates.length === 0) return '-';
                // Filter by selected mood if active
                const filtered = selectedMood
                    ? dates.filter((d: any) => d.mood === selectedMood)
                    : dates;
                if (filtered.length === 0) return '-';
                const sortedDates = [...filtered].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                const visible = sortedDates.slice(0, 3);
                const remaining = sortedDates.length - 3;
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                        {visible.map((d: any, i: number) => (
                            <Tag key={i} color={getMoodColor(d.mood)} style={{ margin: 0, fontSize: 11 }}>
                                {dayjs(d.date).format('MMM D')} - {d.mood}
                            </Tag>
                        ))}
                        {remaining > 0 && (
                            <span
                                title={sortedDates.slice(3).map((d: any) => `${dayjs(d.date).format('MMM D')} - ${d.mood}`).join('\n')}
                                style={{ fontSize: 11, color: '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}
                            >
                                +{remaining} more
                            </span>
                        )}
                    </div>
                );
            },
            sorter: (a: any, b: any) => {
                const aDate = a.response_dates?.[0]?.date || '';
                const bDate = b.response_dates?.[0]?.date || '';
                return aDate.localeCompare(bDate);
            },
        },
    ]

    const handleRefresh = () => {
        Promise.all([
            fetchMoodAnalytics(),
            fetchMoodResponses(),
        ]);
        message.info("Data refreshed");
    };

    const hasData = analyticsData && responses.length > 0;

    // FILTER CARD - Always visible
    const renderFilterCard = () => (
        <Card
            size="small"
            style={{ marginBottom: 20, backgroundColor: '#fafafa' }}
        >
            <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={8}>
                    <div>
                        <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                            <FilterOutlined /> Question Filter
                        </Text>
                        <Select
                            value={selectedQuestion}
                            onChange={setSelectedQuestion}
                            style={{ width: '100%' }}
                            size="small"
                            loading={false}
                            disabled={questions.length === 0}
                        >
                            {questions.length > 0 ? (
                                // Show all questions as options, with first one as default
                                Array.isArray(questions) && questions.map((q) => (
                                    <Option key={q.id} value={q.id}>
                                        {(q.text || q.question_text || 'Untitled').length > 40
                                            ? (q.text || q.question_text || 'Untitled').substring(0, 40) + "..."
                                            : (q.text || q.question_text || 'Untitled')}
                                    </Option>
                                ))
                            ) : (
                                // Show only "No questions available" option when no questions
                                <Option value="no-questions" disabled>
                                    No questions available
                                </Option>
                            )}
                        </Select>
                    </div>
                </Col>

                <Col xs={24} sm={8}>
                    <div>
                        <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                            <CalendarOutlined /> Date Range
                        </Text>
                        <RangePicker
                            value={dateRange}
                            onChange={(dates) => {
                                if (dates && dates[0] && dates[1] && onDateRangeChange) {
                                    onDateRangeChange([dates[0], dates[1]]);
                                }
                            }}
                            style={{ width: '100%' }}
                            size="small"
                            format="MMM D, YYYY"
                        />
                    </div>
                </Col>
                <Col xs={24} sm={8}>
                    <div>
                        <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                            <TeamOutlined /> Department
                        </Text>
                        <Select
                            value={selectedDepartment}
                            onChange={setSelectedDepartment}
                            style={{ width: '100%' }}
                            size="small"
                        >
                            {filteredDepartments.length > 0 ? (
                                filteredDepartments.map((dept: any) => (
                                    <Option key={dept.id || dept.key} value={dept.id || dept.key}>
                                        {dept.Department || dept.name}
                                    </Option>
                                ))
                            ) : (
                                <>
                                    <Option value="all">All Departments</Option>
                                    {departments.map((dept: any) => (
                                        <Option key={dept.id || dept.key} value={dept.id || dept.key}>
                                            {dept.Department || dept.name}
                                        </Option>
                                    ))}
                                </>
                            )}
                        </Select>
                    </div>
                </Col>
            </Row>

            <Row style={{ marginTop: 12 }} justify="end">
                <Col>
                    <Button
                        type="default"
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={handleRefresh}
                        style={{ marginRight: 8 }}
                    >
                        Refresh
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        onClick={handleExportData}
                        loading={exporting}
                    >
                        Export CSV
                    </Button>
                </Col>
            </Row>
        </Card >
    );

    if (loading && !analyticsData) {
        return (
            <div>
                {/* Show filter card even while loading */}
                {renderFilterCard()}
                <div style={{ textAlign: 'center', padding: 50 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>
                        <Text type="secondary">Loading analytics...</Text>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div>
                {renderFilterCard()}

                {hasData ? (
                    <>
                        {/* Statistics Cards */}
                        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                            <Col xs={24} sm={12} md={6}>
                                <Card size="small">
                                    <Statistic
                                        title="Total Responses"
                                        value={analyticsData.total_responses || 0}
                                        prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
                                        valueStyle={{ color: '#1890ff', fontSize: 28 }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Card size="small">
                                    <Statistic
                                        title="Average Mood Score"
                                        value={parseFloat(analyticsData.average_score || 0).toFixed(1)}
                                        suffix="/5"
                                        prefix={<BarChartOutlined style={{ color: '#52c41a' }} />}
                                        valueStyle={{
                                            color: parseFloat(analyticsData.average_score || 0) >= 3.5 ? '#52c41a' :
                                                parseFloat(analyticsData.average_score || 0) >= 2.5 ? '#faad14' : '#ff4d4f',
                                            fontSize: 28
                                        }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Card size="small">
                                    <Statistic
                                        title="Departments"
                                        value={Array.isArray(analyticsData.department_breakdown)
                                            ? analyticsData.department_breakdown.length
                                            : Object.keys(analyticsData.department_breakdown || {}).length}
                                        prefix={<span style={{ color: '#722ed1' }}>🏢</span>}
                                        valueStyle={{ color: '#722ed1', fontSize: 28 }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Charts Section */}
                        <Row gutter={[16, 16]}>
                            {/* Pie Chart */}
                            <Col xs={24} md={12}>
                                <Card
                                    title="Mood Distribution"
                                    size="small"
                                    extra={
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Total: {analyticsData.total_responses}
                                        </Text>
                                    }
                                >
                                    <div style={{ height: 300 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={pieChartData}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                    outerRadius={80}
                                                    fill="#8884d8"
                                                    dataKey="value"
                                                >
                                                    {pieChartData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <RechartsTooltip />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card>
                            </Col>

                            {/* Bar Chart - Daily Average */}
                            <Col xs={24} md={12}>
                                <Card title="Daily Average Score (Last 7 Days)" size="small">
                                    <div style={{ height: 300 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={barChartData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="date" />
                                                <YAxis domain={[0, 5]} />
                                                <RechartsTooltip />
                                                <Legend />
                                                <Bar
                                                    dataKey="avgScore"
                                                    name="Average Score"
                                                    fill="#82ca9d"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card>
                            </Col>

                            {/* Department Comparison */}
                            <Col xs={24}>
                                <Card
                                    title="Department Comparison"
                                    size="small"
                                    extra={
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Sorted by average score
                                        </Text>
                                    }
                                >
                                    <div style={{ height: 300 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={departmentData} layout="vertical">
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis type="number" domain={[0, 5]} />
                                                <YAxis type="category" dataKey="department" width={120} />
                                                <RechartsTooltip />
                                                <Legend />
                                                <Bar
                                                    dataKey="avgScore"
                                                    name="Average Score"
                                                    fill="#8884d8"
                                                    radius={[0, 4, 4, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card>
                            </Col>

                            {/* Mood Trend */}
                            <Col xs={24}>
                                <Card title="Mood Trend Over Time" size="small">
                                    <div style={{ height: 300 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={lineChartData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="date" />
                                                <YAxis domain={[0, 5]} />
                                                <RechartsTooltip />
                                                <Legend />
                                                <Line
                                                    type="monotone"
                                                    dataKey="score"
                                                    name="Average Mood Score"
                                                    stroke="#8884d8"
                                                    strokeWidth={2}
                                                    dot={{ r: 4 }}
                                                    activeDot={{ r: 6 }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card>
                            </Col>
                        </Row>

                        {/* Insights Section */}
                        <Divider />
                        <Card
                            title="Insights"
                            size="small"
                            extra={
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => {
                                        setSelectedMood(null); // Reset mood so ALL users show
                                        setIsUserDetailsModalVisible(true);
                                    }}
                                    icon={<TeamOutlined />}
                                >
                                    View All User Details
                                </Button>
                            }
                        >
                            <Row gutter={[16, 16]}>
                                <Col xs={24} md={12}>
                                    <Title level={5}>Top Moods</Title>
                                    <Text type="secondary" style={{ fontSize: 12, marginBottom: 12, display: 'block' }}>
                                        Click a mood to see only those users
                                    </Text>
                                    {(analyticsData.top_moods || []).map((item: any, index: number) => (
                                        <div
                                            key={index}
                                            style={{
                                                marginBottom: 8,
                                                cursor: 'pointer',
                                                opacity: selectedMood && selectedMood !== item.mood ? 0.6 : 1,
                                                backgroundColor: selectedMood === item.mood ? '#e6f7ff' : 'transparent',
                                                border: selectedMood === item.mood ? '1px solid #91d5ff' : '1px solid transparent',
                                                padding: '6px 10px',
                                                borderRadius: '6px',
                                                transition: 'all 0.2s'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const newMood = item.mood === selectedMood ? null : item.mood;
                                                setSelectedMood(newMood);
                                                setIsUserDetailsModalVisible(true);
                                            }}
                                        >
                                            <Tag
                                                color={getMoodColor(item.mood)}
                                                style={{ minWidth: 100 }}
                                            >
                                                {item.mood}
                                            </Tag>
                                            <Text style={{ marginLeft: 8 }}>
                                                {item.count} responses ({item.percentage}%)
                                            </Text>
                                        </div>
                                    ))}
                                </Col>
                            </Row>
                        </Card>
                    </>
                ) : (
                    /* Empty State - When no data but still show filters */
                    <Card size="small">
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <Empty
                                description={
                                    <div>
                                        <Title level={5}>No Data Available</Title>
                                        <Text type="secondary">
                                            No mood responses found for the selected filters.
                                            <br />
                                            Try adjusting your filters or date range.
                                        </Text>
                                    </div>
                                }
                            />
                        </div>
                    </Card>
                )}
            </div>
            {/* User Details Modal */}
            <Modal
                title={
                    <div>
                        <div className="d-flex align-items-center gap-2">
                            <TeamOutlined />
                            <span>
                                {selectedMood
                                    ? `Users with "${selectedMood}" Mood (${analyticsData?.user_details?.filter((user: any) =>
                                        user.moods && user.moods[selectedMood] && user.moods[selectedMood].count > 0
                                    ).length || 0
                                    })`
                                    : `All Users (${analyticsData?.user_details?.length || 0})`
                                }
                            </span>
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <CalendarOutlined style={{ marginRight: 4 }} />
                            {dateRange[0].format('MMM D, YYYY')} — {dateRange[1].format('MMM D, YYYY')}
                        </Text>
                    </div>
                }
                open={isUserDetailsModalVisible}
                onCancel={() => {
                    setIsUserDetailsModalVisible(false);
                    setSelectedMood(null); // Reset selected mood when closing
                }}
                width={1200}
                footer={[
                    <Button key="close" onClick={() => {
                        setIsUserDetailsModalVisible(false);
                        setSelectedMood(null);
                    }}>
                        Close
                    </Button>
                ]}
            >
                <div style={{ marginTop: 16 }}>
                    <Datatable
                        columns={column}
                        dataSource={selectedMood
                            ? (analyticsData?.user_details?.filter((user: any) => {
                                return user.moods && user.moods[selectedMood] && user.moods[selectedMood].count > 0;
                            }) || [])
                            : (analyticsData?.user_details || [])
                        }
                        Selection={false}
                        showPagination={true}
                    />
                </div>
            </Modal>
        </>
    );
};

export default MoodAnalytics;